import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Minus, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { CustomerPipingRuleRow } from "@/components/customer-piping-rule-row";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { InfoButton, type InfoTopic } from "@/components/info-button";

/** The ⓘ beside each fee switch: what the fee is, in plain words. */
export const FEE_SWITCH_HELP: Record<"admin" | "city" | "permit", InfoTopic> = {
    admin: {
        title: "Admin fee",
        lines: [
            "Our Test Reporting & Compliance fee. It is the same charge in every city.",
            "On means we add it to quotes and invoices. Off means we never add it.",
        ],
    },
    city: {
        title: "City test/filing fee",
        lines: [
            "The fee the city charges when we file a test report.",
            "On means we add it to quotes and invoices. Off means we never add it.",
            "This switch does not cover the permit. The permit has its own switch.",
        ],
    },
    permit: {
        title: "Permit fee",
        lines: [
            "A city permit for replacing or installing a device. On means we add it to quotes.",
            "Off means we never add it. A permit line someone types by hand always stays.",
        ],
    },
};

/**
 * THE PRICING CARD (#1823 slice 5, mockup A).
 *
 * Corbin, 10/9: "I set those on the customer and then don't worry about it on
 * any quote the system creates or I create." Everything a quote reads off the
 * customer sits on this one card — how they're billed, the $ per tech-hour, the
 * test price, the three fee switches, tax, the repair hours by size — with a
 * preview of what a typical quote for them comes out to. Each change saves on
 * its own; there is no form to forget to save.
 *
 * Reads `GET /api/customers/:id/pricing-profile` — the same resolver every
 * quote and invoice line is priced from — so the preview cannot disagree with
 * what a real quote does.
 */

type LaborBand = {
    minSizeIn: number;
    maxSizeIn: number | null;
    hours: number | null;
    techs: number | null;
    unitPrice: number | null;
    source: "contract" | "size_table" | null;
};

type ProfileResponse = {
    profile: {
        name: string | null;
        billingMode: "flat_rate" | "time_and_materials";
        hourlyRate: number | null;
        testingOnly: boolean;
        testFee: number | null;
        fees: { chargeAdminFee: boolean; chargeCityFees: boolean; chargePermitFee: boolean };
        repairLaborRules: Array<{ min_size_in?: number; max_size_in?: number | null; hours: number; techs?: number }>;
        tax: { status: "taxable" | "exempt" | "unknown"; exemptReason: string | null; certRef: string | null };
        label: string | null;
    };
    laborRate: number;
    orgDefaultLaborRate: number;
    laborTable: { repair: LaborBand[]; replace: LaborBand[] };
    typicalQuote: {
        device: string;
        lines: Array<{ key: string; label: string; amount: number | null; charged: boolean }>;
    };
};

type Billing = "contract" | "testing_only" | "flat";

const money = (n: number) => `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
const sizeRange = (b: { minSizeIn: number; maxSizeIn: number | null }) => {
    const f = (n: number) =>
        n === 0.75 ? "¾" : n === 0.5 ? "½" : n === 2.5 ? "2½" : Number.isInteger(n) ? String(n) : String(n);
    if (b.maxSizeIn == null) return `${f(b.minSizeIn)}" and up`;
    if (b.minSizeIn === 0) return `up to ${f(b.maxSizeIn)}"`;
    return `${f(b.minSizeIn)}–${f(b.maxSizeIn)}"`;
};

/** A big on/off switch for one fee. Green = charged, red = not — readable without the words. */
export function FeeSwitch({
    on,
    onChange,
    label,
    hint,
    disabled,
    info,
}: {
    on: boolean;
    onChange: (v: boolean) => void;
    label: string;
    hint: string;
    disabled?: boolean;
    /** The ⓘ beside the switch (kept outside the switch button). */
    info?: InfoTopic;
}) {
    const sw = (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(!on)}
            className="flex w-full items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-2.5 text-left hover:bg-accent/40 disabled:cursor-wait"
        >
            <span
                className={cn(
                    "relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors",
                    on ? "bg-emerald-600" : "bg-red-600",
                )}
            >
                <span
                    className={cn(
                        "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-[left]",
                        on ? "left-[22px]" : "left-0.5",
                    )}
                />
            </span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold">
                    {label}: {on ? "charged" : "not charged"}
                </span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
        </button>
    );
    if (!info) return sw;
    return (
        <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">{sw}</div>
            <InfoButton topic={info} />
        </div>
    );
}

function MoneyField({
    label,
    value,
    onSave,
    disabled,
    testId,
}: {
    label: string;
    value: number | null;
    onSave: (v: number | null) => void;
    disabled?: boolean;
    testId?: string;
}) {
    const [text, setText] = useState(value == null ? "" : String(value));
    useEffect(() => setText(value == null ? "" : String(value)), [value]);
    const commit = () => {
        const v = text.trim() === "" ? null : Number(text);
        if (v != null && (!Number.isFinite(v) || v < 0)) {
            toast.error(`${label}: enter a dollar amount`);
            setText(value == null ? "" : String(value));
            return;
        }
        if (v !== value) onSave(v);
    };
    return (
        <label className="flex items-baseline gap-1">
            <span className="text-2xl font-extrabold tabular-nums">$</span>
            <input
                data-testid={testId}
                aria-label={label}
                inputMode="decimal"
                disabled={disabled}
                className="w-28 rounded-md border border-transparent bg-transparent px-1 text-2xl font-extrabold tabular-nums hover:border-border focus:border-primary focus:outline-none"
                value={text}
                placeholder="—"
                onChange={(e) => setText(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
            />
        </label>
    );
}

export function CustomerPricingCard({ customerId, customerName }: { customerId: string; customerName?: string }) {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery<ProfileResponse>({
        queryKey: ["pricing-profile", customerId],
        queryFn: () => api.get<ProfileResponse>(`/api/customers/${customerId}/pricing-profile`),
        enabled: !!customerId,
    });

    const save = useMutation({
        mutationFn: (body: Record<string, unknown>) => api.patch(`/api/customers/${customerId}/pricing`, body),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ["pricing-profile", customerId] }),
                qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] }),
            ]);
            toast.success("Pricing saved — every new quote uses it");
        },
        onError: (e: any) => {
            qc.invalidateQueries({ queryKey: ["pricing-profile", customerId] });
            toast.error(`NOT saved: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`);
        },
    });

    const [editingHours, setEditingHours] = useState(false);
    const [hoursDraft, setHoursDraft] = useState<LaborBand[]>([]);

    if (isLoading || !data?.profile) {
        return (
            <div className="rounded-xl border border-border/60 bg-card p-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading pricing…
            </div>
        );
    }

    const p = data.profile;
    const who = customerName || p.name || "this customer";
    const billing: Billing = p.billingMode === "flat_rate" ? "flat" : p.testingOnly ? "testing_only" : "contract";
    const pending = save.isPending;
    const permitOn = p.fees.chargePermitFee !== false;

    const setBilling = (b: Billing) => {
        if (b === billing) return;
        if (b === "flat") save.mutate({ billingMode: "flat_rate" });
        else if (b === "testing_only") save.mutate({ billingMode: "time_and_materials", hourlyLaborRate: null });
        else {
            // A contract rate needs a number; start from our standard rate.
            save.mutate({
                billingMode: "time_and_materials",
                hourlyLaborRate: p.hourlyRate ?? data.orgDefaultLaborRate,
            });
        }
    };

    const total = data.typicalQuote.lines
        .filter((l) => l.charged && l.amount != null)
        .reduce((s, l) => s + (l.amount ?? 0), 0);
    const hasOwnHours = p.repairLaborRules.length > 0;

    const startEditHours = () => {
        setHoursDraft(data.laborTable.repair.map((b) => ({ ...b })));
        setEditingHours(true);
    };
    const saveHours = () => {
        save.mutate(
            {
                repairLaborRules: hoursDraft.map((b) => ({
                    minSizeIn: b.minSizeIn,
                    maxSizeIn: b.maxSizeIn,
                    hours: b.hours ?? 1,
                    techs: b.techs ?? 1,
                })),
            },
            { onSuccess: () => setEditingHours(false) },
        );
    };

    const seg = (b: Billing, label: string) => (
        <button
            type="button"
            aria-pressed={billing === b}
            disabled={pending}
            onClick={() => setBilling(b)}
            className={cn(
                "flex-1 px-2 py-2 text-[13px] font-semibold transition-colors",
                billing === b ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-accent",
            )}
        >
            {label}
        </button>
    );

    const box = "flex flex-col gap-2 rounded-lg border border-border/60 bg-card px-3.5 py-3";
    const lbl = "text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground";

    return (
        <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm" data-testid="customer-pricing-card">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-bold tracking-tight">How we charge {who}</h2>
                {pending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Saving" />}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className={box}>
                    <span className={lbl}>Billing</span>
                    <div
                        className="flex overflow-hidden rounded-lg border border-border"
                        role="group"
                        aria-label="Billing"
                    >
                        {seg("contract", "Contract rate")}
                        {seg("testing_only", "Testing only")}
                        {seg("flat", "Our flat prices")}
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {billing === "contract"
                            ? "Their rate on every repair and test."
                            : billing === "testing_only"
                              ? "Their test price; repairs at our standard price."
                              : "Our published prices."}
                    </span>
                </div>
                <div className={box}>
                    <span className={lbl}>Labor · per tech, per hour</span>
                    {billing === "contract" ? (
                        <MoneyField
                            label="Labor per tech-hour"
                            testId="pricing-rate"
                            value={p.hourlyRate}
                            disabled={pending}
                            onSave={(v) => save.mutate({ hourlyLaborRate: v })}
                        />
                    ) : (
                        <span className="text-2xl font-extrabold tabular-nums text-muted-foreground">
                            {money(data.laborRate)}
                        </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                        {billing === "contract" ? "2 techs = 2 × this rate." : "Our standard rate."}
                    </span>
                </div>
                <div className={box}>
                    <span className={lbl}>Test price</span>
                    {billing === "flat" ? (
                        <span className="text-2xl font-extrabold text-muted-foreground">Ours</span>
                    ) : (
                        <MoneyField
                            label="Test price"
                            testId="pricing-test"
                            value={p.testFee}
                            disabled={pending}
                            onSave={(v) => save.mutate({ testFeeFlat: v })}
                        />
                    )}
                    <span className="text-xs text-muted-foreground">Per device tested.</span>
                </div>
                <div className={cn(box, "sm:col-span-2 lg:col-span-2")}>
                    <span className={cn(lbl, "flex items-center gap-1.5")}>
                        <Receipt className="h-3.5 w-3.5" /> Fees
                    </span>
                    <div className="grid gap-2">
                        <FeeSwitch
                            on={p.fees.chargeAdminFee}
                            disabled={pending}
                            onChange={(v) => save.mutate({ chargeAdminFee: v })}
                            label="Admin fee"
                            hint="Our Test Reporting & Compliance fee, every city's version"
                            info={FEE_SWITCH_HELP.admin}
                        />
                        <FeeSwitch
                            on={p.fees.chargeCityFees}
                            disabled={pending}
                            onChange={(v) => save.mutate({ billJurisdictionFees: v })}
                            label="City test/filing fee"
                            hint="The city's fee for filing a test report"
                            info={FEE_SWITCH_HELP.city}
                        />
                        <FeeSwitch
                            on={permitOn}
                            disabled={pending}
                            onChange={(v) => save.mutate({ chargePermitFee: v })}
                            label="Permit fee"
                            hint="The city's permit for replacing or installing a device"
                            info={FEE_SWITCH_HELP.permit}
                        />
                    </div>
                    {(!p.fees.chargeCityFees || !p.fees.chargeAdminFee || !permitOn) && (
                        <span className="text-xs text-muted-foreground">
                            Off = never added to a quote or invoice. Quotes already sent keep theirs.
                        </span>
                    )}
                </div>
                <div className={box}>
                    <span className={lbl}>Tax</span>
                    <span
                        className={cn(
                            "text-xl font-extrabold",
                            p.tax.status === "unknown" && "text-amber-700 dark:text-amber-400",
                        )}
                    >
                        {p.tax.status === "exempt" ? "Exempt" : p.tax.status === "taxable" ? "Taxable" : "Not set"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {p.tax.status === "exempt"
                            ? [p.tax.exemptReason, p.tax.certRef ? "certificate on file" : null]
                                  .filter(Boolean)
                                  .join(" · ") || "Exempt"
                            : p.tax.status === "unknown"
                              ? "Set it on the customer's details before sending quotes."
                              : "Sales tax is added."}
                    </span>
                </div>
            </div>

            <CustomerPipingRuleRow customerId={customerId} />

            <details className="mt-3 rounded-lg border border-border/60 bg-card px-3.5 py-2.5" open={editingHours}>
                <summary className="cursor-pointer text-sm font-semibold text-primary">
                    Repair hours by size {hasOwnHours ? "· their own hours" : "· our standard"}
                </summary>
                <div className="mt-2 overflow-x-auto">
                    <table className="w-full min-w-[420px] text-sm">
                        <thead>
                            <tr className="text-left text-[11.5px] uppercase tracking-wide text-muted-foreground">
                                <th className="py-1.5 pr-3 font-semibold">Size</th>
                                <th className="py-1.5 pr-3 font-semibold">Repair</th>
                                <th className="py-1.5 pr-3 font-semibold">Replace</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(editingHours ? hoursDraft : data.laborTable.repair).map((b, i) => {
                                const rep = data.laborTable.replace.find(
                                    (r) =>
                                        (b.maxSizeIn ?? 99) >= r.minSizeIn &&
                                        (r.maxSizeIn == null || (b.maxSizeIn ?? 99) <= r.maxSizeIn),
                                );
                                return (
                                    <tr key={`${b.minSizeIn}-${b.maxSizeIn}`} className="border-t border-border/50">
                                        <td className="py-1.5 pr-3 font-medium">{sizeRange(b)}</td>
                                        <td className="py-1.5 pr-3 tabular-nums">
                                            {editingHours ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <input
                                                        aria-label={`Hours for ${sizeRange(b)}`}
                                                        inputMode="decimal"
                                                        className="w-14 rounded border border-border bg-background px-1 py-0.5 text-right"
                                                        value={b.hours ?? ""}
                                                        onChange={(e) =>
                                                            setHoursDraft((d) =>
                                                                d.map((x, j) =>
                                                                    j === i
                                                                        ? {
                                                                              ...x,
                                                                              hours: Number(e.target.value) || null,
                                                                          }
                                                                        : x,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                    h ×
                                                    <span className="inline-flex items-center overflow-hidden rounded border border-border">
                                                        <button
                                                            type="button"
                                                            aria-label="Fewer techs"
                                                            className="px-1.5 py-0.5 hover:bg-accent"
                                                            onClick={() =>
                                                                setHoursDraft((d) =>
                                                                    d.map((x, j) =>
                                                                        j === i
                                                                            ? {
                                                                                  ...x,
                                                                                  techs: Math.max(
                                                                                      1,
                                                                                      (x.techs ?? 1) - 1,
                                                                                  ),
                                                                              }
                                                                            : x,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            <Minus className="h-3 w-3" />
                                                        </button>
                                                        <span className="px-1.5">{b.techs ?? 1}</span>
                                                        <button
                                                            type="button"
                                                            aria-label="More techs"
                                                            className="px-1.5 py-0.5 hover:bg-accent"
                                                            onClick={() =>
                                                                setHoursDraft((d) =>
                                                                    d.map((x, j) =>
                                                                        j === i
                                                                            ? {
                                                                                  ...x,
                                                                                  techs: Math.min(
                                                                                      10,
                                                                                      (x.techs ?? 1) + 1,
                                                                                  ),
                                                                              }
                                                                            : x,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                    techs
                                                </span>
                                            ) : (
                                                `${b.hours ?? "—"} h × ${b.techs ?? 1} tech${(b.techs ?? 1) === 1 ? "" : "s"}`
                                            )}
                                        </td>
                                        <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">
                                            {rep ? `${rep.hours} h × ${rep.techs} techs` : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {editingHours ? (
                        <>
                            <Button size="sm" onClick={saveHours} disabled={pending}>
                                Save hours for {who}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingHours(false)}
                                disabled={pending}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button size="sm" variant="outline" onClick={startEditHours} disabled={pending}>
                                Change hours for {who}
                            </Button>
                            {hasOwnHours && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => save.mutate({ repairLaborRules: [] })}
                                    disabled={pending}
                                >
                                    Back to our standard hours
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </details>

            <div className="mt-3 rounded-lg bg-secondary px-3.5 py-3" data-testid="pricing-typical-quote">
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-bold">A typical quote for them</span>
                    <span className="text-xs text-muted-foreground">{data.typicalQuote.device}</span>
                </div>
                <div className="flex flex-col gap-1 text-[13.5px] tabular-nums">
                    {data.typicalQuote.lines.map((l) => (
                        <div key={l.key} className="flex justify-between gap-3">
                            <span className={cn(!l.charged && "text-muted-foreground line-through")}>{l.label}</span>
                            <span className={cn(!l.charged && "text-muted-foreground")}>
                                {!l.charged ? "not charged" : l.amount == null ? "varies" : money(l.amount)}
                            </span>
                        </div>
                    ))}
                    <div className="mt-1 flex justify-between gap-3 border-t border-border pt-1.5 font-bold">
                        <span>Known so far</span>
                        <span>{money(total)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
